"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReviewsSection } from "./reviews-section";
import { useRenderedContract } from "@/hooks/use-rendered-contract";
import { buildInstallmentRows } from "@/lib/installment";
import { formatCurrency } from "@/lib/utils";
import type { StoreConfig } from "@/types";

interface ProductTabsProps {
  slug: string;
  price: number;
  description?: string;
}

export function ProductTabs({ slug, price, description }: ProductTabsProps) {
  const [reviewCount, setReviewCount] = useState<number | null>(null);
  const { data: deliveryContract } = useRenderedContract("delivery_info");

  const { data: config } = useQuery<StoreConfig>({
    queryKey: ["store-config"],
    queryFn: async () => {
      const res = await api.get("/api/store/config");
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const productInfo = config?.productInfo ?? {};
  const warranty = productInfo["product_info.warranty_delivery_content"]?.trim();
  const installmentEnabled = productInfo["product_info.installment_enabled"] === "true";
  const installmentNote = productInfo["product_info.installment_note"]?.trim();
  const installmentRows = installmentEnabled ? buildInstallmentRows(price, productInfo) : [];
  const showInstallment = installmentEnabled && installmentRows.length > 1;

  return (
    <div className="mt-10 border-t border-gray-100 pt-8 md:mt-16 md:pt-12">
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Ürün Detayları</TabsTrigger>
          <TabsTrigger value="reviews">
            Değerlendirmeler{reviewCount != null ? ` (${reviewCount})` : ""}
          </TabsTrigger>
          <TabsTrigger value="warranty">Garanti ve Teslimat</TabsTrigger>
          {deliveryContract && <TabsTrigger value="delivery">Teslimat Bilgileri</TabsTrigger>}
          {showInstallment && <TabsTrigger value="installment">Taksit Tablosu</TabsTrigger>}
        </TabsList>

        <TabsContent value="details">
          {description ? (
            <div
              className="prose prose-sm md:prose-base max-w-none text-gray-600 font-light prose-headings:text-gray-900 prose-headings:font-bold prose-headings:tracking-tight prose-a:text-black hover:prose-a:text-gray-600"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : (
            <p className="text-sm text-gray-500">Bu ürün için detaylı açıklama bulunmuyor.</p>
          )}
        </TabsContent>

        <TabsContent value="reviews">
          <ReviewsSection slug={slug} onCountChange={setReviewCount} />
        </TabsContent>

        <TabsContent value="warranty">
          {warranty ? (
            <div
              className="prose prose-sm md:prose-base max-w-none text-gray-600 font-light prose-headings:text-gray-900 prose-headings:font-bold prose-a:text-black hover:prose-a:text-gray-600"
              dangerouslySetInnerHTML={{ __html: warranty }}
            />
          ) : (
            <p className="text-sm text-gray-500">Garanti ve teslimat bilgisi yakında eklenecek.</p>
          )}
        </TabsContent>

        {deliveryContract && (
          <TabsContent value="delivery">
            <div
              className="prose prose-sm md:prose-base max-w-none text-gray-600 font-light prose-headings:text-gray-900 prose-headings:font-bold prose-a:text-black hover:prose-a:text-gray-600"
              dangerouslySetInnerHTML={{ __html: deliveryContract.content }}
            />
          </TabsContent>
        )}

        {showInstallment && (
          <TabsContent value="installment">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[360px] text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-3 pr-4 font-semibold">Taksit</th>
                    <th className="py-3 pr-4 font-semibold">Aylık Tutar</th>
                    <th className="py-3 font-semibold">Toplam Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {installmentRows.map((row) => (
                    <tr key={row.months} className="border-b border-gray-100">
                      <td className="py-3 pr-4 font-medium text-gray-900">
                        {row.months === 1 ? "Tek Çekim" : `${row.months} Taksit`}
                        {row.months > 1 && row.interestFree && (
                          <span className="ml-2 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                            Komisyonsuz
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-gray-700">{formatCurrency(row.monthly)}</td>
                      <td className="py-3 font-semibold text-gray-900">{formatCurrency(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {installmentNote && (
              <p className="mt-4 text-xs text-gray-500">{installmentNote}</p>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
